/* BillingOperations — /admin/billing-operations
   Phase 5: Prebill queue, invoice management, and AR aging report.
   Three tabs:
     1. Prebill Queue  — approved time entries / expenses not yet invoiced
     2. Invoices       — list all invoices, create new, update status
     3. AR Report      — accounts-receivable aging summary + detail
*/
import { useState } from 'react';
import css from '../AdminPage.module.css';
import EmptyState from '../../../components/ui/EmptyState/EmptyState';
import { useToast } from '../../../hooks/useToast';
import {
  useActivityCodes,
  useCreateActivityCode,
  useUpdateActivityCode,
  type ActivityCode,
  type ActivityCodeInput,
} from '../../../hooks/useActivityCodes';
import {
  usePrebillQueue,
  useInvoices,
  useInvoice,
  useARReport,
  useCreateInvoice,
  useUpdateInvoiceStatus,
  useAddPayment,
  useAddAdjustment,
  useRemoveInvoiceLine,
  type AddPaymentInput,
  type AddAdjustmentInput,
} from '../../../hooks/useInvoices';

type Tab = 'prebill' | 'invoices' | 'ar' | 'config';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(amount: number, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  draft: 'badgeNeutral',
  sent: 'badgeInfo',
  paid: 'badgeSuccess',
  void: 'badgeError',
  write_off: 'badgeWarning',
};

function statusBadge(status: string) {
  const cls = STATUS_BADGE_CLASS[status] ?? 'badgeNeutral';
  return <span className={css[cls as keyof typeof css]}>{status.replace('_', ' ')}</span>;
}

// ─── Prebill Queue tab ────────────────────────────────────────────────────────

function PrebillQueueTab() {
  const { data, isLoading } = usePrebillQueue();
  const createInvoice = useCreateInvoice();
  const { showToast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [caseRef, setCaseRef] = useState('');

  const { data: filtered } = usePrebillQueue(caseRef || undefined);
  const queue = caseRef ? filtered : data;

  const timeEntries = queue?.timeEntries ?? [];
  const expenses = queue?.expenses ?? [];
  const allItems = [
    ...timeEntries.map((t) => ({ ...t, _key: `te-${t.id}`, type: 'time_entry' as const })),
    ...expenses.map((e) => ({ ...e, _key: `ex-${e.id}`, type: 'expense' as const })),
  ];

  const toggle = (key: string) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const handleCreate = async () => {
    const items = allItems
      .filter((i) => selected.has(i._key))
      .map((i) => ({ type: i.type, id: i.id }));
    if (items.length === 0) {
      showToast('Select at least one item', 'warning');
      return;
    }
    try {
      const invoice = await createInvoice.mutateAsync({
        caseRef: items[0] ? (allItems.find((i) => selected.has(i._key))?.caseRef ?? '') : '',
        items,
      });
      showToast(`Invoice ${invoice.invoiceNumber} created`, 'success');
      setSelected(new Set());
    } catch {
      showToast('Failed to create invoice', 'error');
    }
  };

  if (isLoading) return <p className={css.mutedText}>Loading prebill queue…</p>;

  return (
    <div>
      <div className={css.filterBar}>
        <input
          className={css.formInput}
          placeholder="Filter by case ref…"
          value={caseRef}
          onChange={(e) => setCaseRef(e.target.value)}
          style={{ maxWidth: 240 }}
        />
        <button
          className={css.btnPrimary}
          onClick={handleCreate}
          disabled={selected.size === 0 || createInvoice.isPending}
        >
          + Create Invoice from Selected ({selected.size})
        </button>
      </div>

      {allItems.length === 0 ? (
        <EmptyState message="No approved billable items awaiting invoicing." />
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>Type</th>
              <th>Case</th>
              <th>Description</th>
              <th>Date</th>
              <th>Amount / Units</th>
            </tr>
          </thead>
          <tbody>
            {allItems.map((item) => (
              <tr key={item._key}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(item._key)}
                    onChange={() => toggle(item._key)}
                  />
                </td>
                <td style={{ textTransform: 'capitalize' }}>{item.type.replace('_', ' ')}</td>
                <td>{item.caseRef}</td>
                <td>{item.taskDescription ?? item.description ?? '—'}</td>
                <td>
                  {item.type === 'time_entry'
                    ? item.startedAt
                      ? new Date(item.startedAt).toLocaleDateString('en-GB')
                      : '—'
                    : item.expenseDate
                      ? new Date(item.expenseDate).toLocaleDateString('en-GB')
                      : '—'}
                </td>
                <td>
                  {item.type === 'time_entry'
                    ? `${item.units ?? 0} units${item.rateApplied ? ` @ ${fmt(item.rateApplied)}` : ''}`
                    : item.amount != null
                      ? fmt(item.amount, item.currency)
                      : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Invoice detail panel ─────────────────────────────────────────────────────

function InvoiceDetail({ invoiceId, onBack }: { invoiceId: number; onBack: () => void }) {
  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const updateStatus = useUpdateInvoiceStatus();
  const addPayment = useAddPayment();
  const addAdjustment = useAddAdjustment();
  const removeLine = useRemoveInvoiceLine();
  const { showToast } = useToast();

  const [payForm, setPayForm] = useState<Partial<AddPaymentInput>>({});
  const [adjForm, setAdjForm] = useState<Partial<AddAdjustmentInput>>({});
  const [showPayForm, setShowPayForm] = useState(false);
  const [showAdjForm, setShowAdjForm] = useState(false);

  if (isLoading || !invoice) return <p className={css.mutedText}>Loading…</p>;

  const outstanding = invoice.totalAmount - invoice.amountPaid - invoice.amountAdjusted;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateStatus.mutateAsync({ id: invoice.id, status: newStatus });
      showToast(`Invoice marked as ${newStatus}`, 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to update status', 'error');
    }
  };

  const handlePayment = async () => {
    if (!payForm.paymentDate || !payForm.amount) {
      showToast('Payment date and amount are required', 'warning');
      return;
    }
    try {
      await addPayment.mutateAsync({ id: invoice.id, payment: payForm as AddPaymentInput });
      showToast('Payment posted', 'success');
      setPayForm({});
      setShowPayForm(false);
    } catch {
      showToast('Failed to post payment', 'error');
    }
  };

  const handleAdjustment = async () => {
    if (!adjForm.adjustmentType || !adjForm.amount || !adjForm.reason) {
      showToast('Type, amount, and reason are required', 'warning');
      return;
    }
    try {
      await addAdjustment.mutateAsync({
        id: invoice.id,
        adjustment: adjForm as AddAdjustmentInput,
      });
      showToast('Adjustment added', 'success');
      setAdjForm({});
      setShowAdjForm(false);
    } catch {
      showToast('Failed to add adjustment', 'error');
    }
  };

  return (
    <div>
      <button className={css.btnSecondary} onClick={onBack} style={{ marginBottom: 16 }}>
        ← Back to Invoices
      </button>

      <div className={css.sectionHeader}>
        <div>
          <h2 className={css.sectionTitle}>{invoice.invoiceNumber}</h2>
          <p className={css.sectionSubtitle}>
            Case: <strong>{invoice.caseRef}</strong>
            {invoice.clientName && ` — ${invoice.clientName}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {statusBadge(invoice.status)}
          {invoice.status === 'draft' && (
            <button className={css.btnPrimary} onClick={() => handleStatusChange('sent')}>
              Mark Sent
            </button>
          )}
          {invoice.status === 'sent' && (
            <>
              <button className={css.btnPrimary} onClick={() => handleStatusChange('paid')}>
                Mark Paid
              </button>
              <button className={css.btnDanger} onClick={() => handleStatusChange('void')}>
                Void
              </button>
            </>
          )}
        </div>
      </div>

      <div className={css.statsGrid}>
        {[
          ['Total', fmt(invoice.totalAmount, invoice.currency)],
          ['Paid', fmt(invoice.amountPaid, invoice.currency)],
          ['Outstanding', fmt(outstanding, invoice.currency)],
        ].map(([label, value]) => (
          <div key={label} className={css.statCard}>
            <div className={css.statLabel}>{label}</div>
            <div className={css.statValue}>{value}</div>
          </div>
        ))}
      </div>

      {/* Lines */}
      <h3 className={css.subHeading}>Lines</h3>
      {(invoice.lines?.length ?? 0) === 0 ? (
        <p className={css.mutedText}>No lines on this invoice.</p>
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines!.map((l) => (
              <tr key={l.id}>
                <td style={{ textTransform: 'capitalize' }}>{l.lineType.replace('_', ' ')}</td>
                <td>{l.description}</td>
                <td>{l.quantity}</td>
                <td>{fmt(l.unitRate, invoice.currency)}</td>
                <td>{fmt(l.lineTotal, invoice.currency)}</td>
                <td>
                  {invoice.status === 'draft' && (
                    <button
                      className={`${css.btnDanger} ${css.btnSmall}`}
                      onClick={async () => {
                        try {
                          await removeLine.mutateAsync({ invoiceId: invoice.id, lineId: l.id });
                        } catch {
                          showToast('Failed to remove line', 'error');
                        }
                      }}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Payments */}
      <h3 className={css.subHeading}>Payments</h3>
      {(invoice.payments?.length ?? 0) === 0 ? (
        <p className={css.mutedText}>No payments recorded.</p>
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            {invoice.payments!.map((p) => (
              <tr key={p.id}>
                <td>{new Date(p.paymentDate).toLocaleDateString('en-GB')}</td>
                <td>{fmt(p.amount, invoice.currency)}</td>
                <td>{p.paymentMethod ?? '—'}</td>
                <td>{p.reference ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {invoice.status === 'sent' && !showPayForm && (
        <button
          className={css.btnPrimary}
          style={{ marginTop: 8 }}
          onClick={() => setShowPayForm(true)}
        >
          + Post Payment
        </button>
      )}
      {showPayForm && (
        <div className={css.formCard} style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="date"
              className={css.formInput}
              placeholder="Payment date"
              value={payForm.paymentDate ?? ''}
              onChange={(e) => setPayForm((p) => ({ ...p, paymentDate: e.target.value }))}
            />
            <input
              type="number"
              className={css.formInput}
              placeholder="Amount"
              min={0}
              step="0.01"
              value={payForm.amount ?? ''}
              onChange={(e) => setPayForm((p) => ({ ...p, amount: parseFloat(e.target.value) }))}
            />
            <input
              className={css.formInput}
              placeholder="Method (BACS, card…)"
              value={payForm.paymentMethod ?? ''}
              onChange={(e) => setPayForm((p) => ({ ...p, paymentMethod: e.target.value }))}
            />
            <input
              className={css.formInput}
              placeholder="Reference"
              value={payForm.reference ?? ''}
              onChange={(e) => setPayForm((p) => ({ ...p, reference: e.target.value }))}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              className={css.btnPrimary}
              onClick={handlePayment}
              disabled={addPayment.isPending}
            >
              Save
            </button>
            <button className={css.btnSecondary} onClick={() => setShowPayForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Adjustments */}
      <h3 className={css.subHeading}>Adjustments</h3>
      {(invoice.adjustments?.length ?? 0) === 0 ? (
        <p className={css.mutedText}>No adjustments recorded.</p>
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount</th>
              <th>Reason</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {invoice.adjustments!.map((a) => (
              <tr key={a.id}>
                <td style={{ textTransform: 'capitalize' }}>
                  {a.adjustmentType.replace('_', ' ')}
                </td>
                <td>{fmt(a.amount, invoice.currency)}</td>
                <td>{a.reason}</td>
                <td>{a.createdBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {['draft', 'sent'].includes(invoice.status) && !showAdjForm && (
        <button
          className={css.btnSecondary}
          style={{ marginTop: 8 }}
          onClick={() => setShowAdjForm(true)}
        >
          + Add Adjustment
        </button>
      )}
      {showAdjForm && (
        <div className={css.formCard} style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              className={css.formSelect}
              value={adjForm.adjustmentType ?? ''}
              onChange={(e) =>
                setAdjForm((p) => ({
                  ...p,
                  adjustmentType: e.target.value as AddAdjustmentInput['adjustmentType'],
                }))
              }
            >
              <option value="">— Type —</option>
              <option value="discount">Discount</option>
              <option value="write_off">Write-off</option>
              <option value="credit">Credit</option>
              <option value="reversal">Reversal</option>
            </select>
            <input
              type="number"
              className={css.formInput}
              placeholder="Amount"
              min={0}
              step="0.01"
              value={adjForm.amount ?? ''}
              onChange={(e) => setAdjForm((p) => ({ ...p, amount: parseFloat(e.target.value) }))}
            />
            <input
              className={css.formInput}
              placeholder="Reason (required)"
              value={adjForm.reason ?? ''}
              onChange={(e) => setAdjForm((p) => ({ ...p, reason: e.target.value }))}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              className={css.btnPrimary}
              onClick={handleAdjustment}
              disabled={addAdjustment.isPending}
            >
              Save
            </button>
            <button className={css.btnSecondary} onClick={() => setShowAdjForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Invoices tab ─────────────────────────────────────────────────────────────

function InvoicesTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const [caseRefFilter, setCaseRefFilter] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data: invoices = [], isLoading } = useInvoices({
    caseRef: caseRefFilter || undefined,
    status: statusFilter || undefined,
  });

  if (selectedId !== null) {
    return <InvoiceDetail invoiceId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div>
      <div className={css.filterBar}>
        <input
          className={css.formInput}
          placeholder="Filter by case ref…"
          value={caseRefFilter}
          onChange={(e) => setCaseRefFilter(e.target.value)}
          style={{ maxWidth: 200 }}
        />
        <select
          className={css.formSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ maxWidth: 160 }}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="paid">Paid</option>
          <option value="void">Void</option>
          <option value="write_off">Write-off</option>
        </select>
      </div>

      {isLoading ? (
        <p className={css.mutedText}>Loading…</p>
      ) : invoices.length === 0 ? (
        <EmptyState message="No invoices found." />
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Case</th>
              <th>Client</th>
              <th>Status</th>
              <th>Total</th>
              <th>Outstanding</th>
              <th>Due</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const outstanding = inv.totalAmount - inv.amountPaid - inv.amountAdjusted;
              return (
                <tr key={inv.id}>
                  <td>
                    <code>{inv.invoiceNumber}</code>
                  </td>
                  <td>{inv.caseRef}</td>
                  <td>{inv.clientName ?? '—'}</td>
                  <td>{statusBadge(inv.status)}</td>
                  <td>{fmt(inv.totalAmount, inv.currency)}</td>
                  <td
                    style={{
                      color: outstanding > 0 ? 'var(--error-600)' : 'var(--success-600)',
                      fontWeight: 600,
                    }}
                  >
                    {fmt(outstanding, inv.currency)}
                  </td>
                  <td>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-GB') : '—'}</td>
                  <td>
                    <button
                      className={`${css.btnSecondary} ${css.btnSmall}`}
                      onClick={() => setSelectedId(inv.id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── AR Report tab ────────────────────────────────────────────────────────────

const BUCKET_ORDER = ['current', '1-30', '31-60', '61-90', '90+'] as const;
const BUCKET_LABELS: Record<string, string> = {
  current: 'Current (not yet due)',
  '1-30': '1–30 days overdue',
  '31-60': '31–60 days overdue',
  '61-90': '61–90 days overdue',
  '90+': '90+ days overdue',
};

function ARReportTab() {
  const [caseRef, setCaseRef] = useState('');
  const { data, isLoading } = useARReport(caseRef || undefined);

  const items = data?.items ?? [];
  const summary = data?.summary ?? {};

  return (
    <div>
      <div className={css.filterBar}>
        <input
          className={css.formInput}
          placeholder="Filter by case ref…"
          value={caseRef}
          onChange={(e) => setCaseRef(e.target.value)}
          style={{ maxWidth: 240 }}
        />
      </div>

      {/* Summary buckets */}
      <div className={css.statsGrid}>
        {BUCKET_ORDER.map((b) => {
          const s = summary[b] ?? { count: 0, outstanding: 0 };
          const isOverdue = b !== 'current';
          return (
            <div
              key={b}
              className={`${css.statCard} ${isOverdue ? css.statCardAccentDanger : css.statCardAccentSuccess}`}
            >
              <div className={css.statLabel}>{BUCKET_LABELS[b]}</div>
              <div className={css.statValue}>{fmt(s.outstanding)}</div>
              <div className={css.mutedText}>
                {s.count} invoice{s.count !== 1 ? 's' : ''}
              </div>
            </div>
          );
        })}
      </div>

      {isLoading ? (
        <p className={css.mutedText}>Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState message="No outstanding invoices." />
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Case</th>
              <th>Client</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Outstanding</th>
              <th>Age Bucket</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id}>
                <td>
                  <code>{row.invoiceNumber}</code>
                </td>
                <td>{row.caseRef}</td>
                <td>{row.clientName ?? '—'}</td>
                <td>{statusBadge(row.status)}</td>
                <td>{row.dueDate ? new Date(row.dueDate).toLocaleDateString('en-GB') : '—'}</td>
                <td
                  style={{
                    color: row.ageBucket !== 'current' ? 'var(--error-600)' : 'var(--success-600)',
                    fontWeight: 600,
                  }}
                >
                  {fmt(row.outstandingAmount)}
                </td>
                <td>
                  <span
                    className={
                      row.ageBucket === 'current'
                        ? css.badgeSuccess
                        : row.ageBucket === '90+'
                          ? css.badgeError
                          : css.badgeWarning
                    }
                  >
                    {row.ageBucket}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Activity Codes tab ───────────────────────────────────────────────────────

const BLANK_FORM: ActivityCodeInput = {
  code: '',
  name: '',
  description: '',
  billableDefault: true,
};

function ActivityCodesTab() {
  const { showToast } = useToast();
  const [showInactive, setShowInactive] = useState(false);
  const { data: codes = [], isLoading } = useActivityCodes(showInactive);
  const create = useCreateActivityCode();
  const update = useUpdateActivityCode();

  const [editing, setEditing] = useState<ActivityCode | null>(null);
  const [form, setForm] = useState<ActivityCodeInput>(BLANK_FORM);
  const [showForm, setShowForm] = useState(false);

  function openNew() {
    setEditing(null);
    setForm(BLANK_FORM);
    setShowForm(true);
  }

  function openEdit(ac: ActivityCode) {
    setEditing(ac);
    setForm({
      code: ac.code,
      name: ac.name,
      description: ac.description ?? '',
      billableDefault: ac.billableDefault,
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.code.trim() || !form.name.trim()) {
      showToast('Code and name are required', 'warning');
      return;
    }
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, ...form });
        showToast('Activity code updated', 'success');
      } else {
        await create.mutateAsync(form);
        showToast('Activity code created', 'success');
      }
      setShowForm(false);
    } catch {
      showToast('Save failed', 'error');
    }
  }

  async function toggleActive(ac: ActivityCode) {
    try {
      await update.mutateAsync({ id: ac.id, active: !ac.active } as Parameters<
        typeof update.mutateAsync
      >[0]);
      showToast(ac.active ? 'Deactivated' : 'Activated', 'success');
    } catch {
      showToast('Update failed', 'error');
    }
  }

  return (
    <div>
      <div className={css.filterBar}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-secondary)',
          }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
        <button className={css.btnPrimary} onClick={openNew}>
          + New Activity Code
        </button>
      </div>

      {showForm && (
        <div className={css.formCard} style={{ marginBottom: 'var(--space-4)' }}>
          <p className={css.subHeading}>{editing ? 'Edit Activity Code' : 'New Activity Code'}</p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-3)',
            }}
          >
            <div className={css.formGroup}>
              <label className={css.formLabel}>Code *</label>
              <input
                className={css.formInput}
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="e.g. L110"
              />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Name *</label>
              <input
                className={css.formInput}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Fact Investigation"
              />
            </div>
          </div>
          <div className={css.formGroup} style={{ marginBottom: 'var(--space-3)' }}>
            <label className={css.formLabel}>Description</label>
            <input
              className={css.formInput}
              value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
            />
          </div>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-3)',
            }}
          >
            <input
              type="checkbox"
              checked={form.billableDefault}
              onChange={(e) => setForm((f) => ({ ...f, billableDefault: e.target.checked }))}
            />
            Billable by default
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className={css.btnPrimary}
              onClick={handleSave}
              disabled={create.isPending || update.isPending}
            >
              {create.isPending || update.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className={css.btnSecondary} onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className={css.mutedText}>Loading activity codes…</p>
      ) : codes.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No activity codes"
          message="Create your first activity code above."
        />
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Description</th>
              <th>Billable default</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((ac) => (
              <tr key={ac.id} style={{ opacity: ac.active ? 1 : 0.5 }}>
                <td>
                  <code
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      background: 'var(--surface-tertiary)',
                      padding: '2px 6px',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    {ac.code}
                  </code>
                </td>
                <td>{ac.name}</td>
                <td className={css.mutedText}>{ac.description ?? '—'}</td>
                <td>
                  <span className={ac.billableDefault ? css.badgeSuccess : css.badgeNeutral}>
                    {ac.billableDefault ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <span className={ac.active ? css.badgeSuccess : css.badgeNeutral}>
                    {ac.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button
                    className={`${css.btnSecondary} ${css.btnSmall}`}
                    onClick={() => openEdit(ac)}
                  >
                    Edit
                  </button>
                  <button
                    className={`${css.btnSecondary} ${css.btnSmall}`}
                    onClick={() => toggleActive(ac)}
                  >
                    {ac.active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function BillingOperations() {
  const [tab, setTab] = useState<Tab>('prebill');

  const TABS: { id: Tab; label: string }[] = [
    { id: 'prebill', label: 'Prebill Queue' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'ar', label: 'AR Report' },
    { id: 'config', label: 'Activity Codes' },
  ];

  return (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <div>
          <h2 className={css.sectionTitle}>Billing Operations</h2>
          <p className={css.sectionSubtitle}>
            Prebill queue, invoice management, AR aging, and billing config
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className={css.tabs}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={tab === t.id ? css.tabActive : css.tab}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'prebill' && <PrebillQueueTab />}
      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'ar' && <ARReportTab />}
      {tab === 'config' && <ActivityCodesTab />}
    </div>
  );
}
